import type React from 'react';
import { useMemo, useState, useEffect, useRef } from 'react';

/**
 * 一覧をクライアント側でページ分割するためのフック。
 *
 * API は全件を返しているため、検索・ソートは「全件に対して」適用したうえで、
 * その結果をこのフックで切り出して表示する。
 * サーバー側ページングにすると検索・ソートもサーバーへ移す必要があるので、
 * ここでは表示の分割だけを担当する。
 */
export interface UsePaginationResult<T> {
  /** 現在ページに表示する要素 */
  pageItems: T[];
  /** 1始まりの現在ページ */
  page: number;
  /** 総ページ数（0件でも1を返す） */
  totalPages: number;
  /** 絞り込み後の総件数 */
  totalItems: number;
  /** 現在ページ先頭の通し番号（1始まり、0件なら0） */
  startIndex: number;
  /** 現在ページ末尾の通し番号（1始まり、0件なら0） */
  endIndex: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export interface UsePaginationOptions {
  /**
   * 検索語・絞り込み・ソートなど「並びが変わる操作」を表す文字列。
   * これが変わったら1ページ目に戻す。
   * データ再取得（ポーリング）では変わらないようにしておくこと。
   * ここに入れないと、3ページ目を見ている状態で検索したときに
   * 3ページ目のまま先頭が見えない、という挙動になる。
   */
  resetKey?: string;
  initialPageSize?: number;
  /**
   * ページが変わったときに、この要素の先頭までスクロールする。
   * 一覧の下端にあるページャを押すと、そのままでは新しいページの
   * 「途中」が見えている状態になるため、一覧の先頭へ戻す。
   */
  scrollTargetRef?: React.RefObject<HTMLElement | null>;
}

export function usePagination<T>(
  items: T[],
  options: UsePaginationOptions = {}
): UsePaginationResult<T> {
  const { resetKey, initialPageSize = DEFAULT_PAGE_SIZE, scrollTargetRef } = options;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // 検索・絞り込みで件数が減ったとき、現在ページが存在しなくなることがある
  // （例: 3ページ目を見ている状態で検索して1ページ分になる）。
  // その場合に空表示のまま固まらないよう、最終ページへ寄せる。
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);

  // 初回描画では動かさない（読み込み直後に勝手にスクロールしないように）
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const target = scrollTargetRef?.current;
    // jsdom など scrollIntoView を持たない環境があるので存在確認してから呼ぶ
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'start' });
    }
  }, [page, pageSize, scrollTargetRef]);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  return {
    pageItems,
    page: safePage,
    totalPages,
    totalItems,
    startIndex: totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1,
    endIndex: totalItems === 0 ? 0 : (safePage - 1) * pageSize + pageItems.length,
    pageSize,
    setPage,
    setPageSize: (size: number) => {
      setPageSize(size);
      // 表示件数を変えたら通し番号がずれるので先頭に戻す
      setPage(1);
    },
  };
}
